<template>
  <div>
    <Dialog :commonDialog="dialogStatus" :dialogWidth="dialogWidth">
      <template v-slot:title>
        <div class="d-flex align-center width-100">
          <v-icon color="cyan darken-2" class="mr-2">mdi-barcode-scan</v-icon>
          <span class="font-weight-700 color-custom-blue"
            >Serial Numbers : {{ getProductName() }}</span
          >
          <v-spacer></v-spacer>
          <v-btn
            icon
            aria-label="Close serial numbers dialog"
            v-on:click="$emit('close')"
          >
            <v-icon color="grey darken-1">mdi-close</v-icon>
          </v-btn>
        </div>
      </template>

      <template v-slot:body>
        <div class="px-4">
          <!-- SUMMARY HEADER KPI BAR -->
          <div
            class="grey lighten-4 custom-grey-border rounded-lg pa-3 mb-4 d-flex align-center justify-space-between flex-wrap ga-2"
          >
            <div class="d-flex align-center">
              <v-avatar color="cyan lighten-5" size="32" class="mr-2">
                <v-icon color="cyan darken-2" small>mdi-package-variant</v-icon>
              </v-avatar>
              <span class="caption-1 font-weight-600 grey--text text--darken-3">
                Total Available Qty:
                <strong class="color-custom-blue font-weight-700">{{
                  pendingQty
                }}</strong>
              </span>
            </div>

            <div class="d-flex align-center">
              <v-avatar color="teal lighten-5" size="32" class="mr-2">
                <v-icon color="teal darken-2" small>mdi-counter</v-icon>
              </v-avatar>
              <span class="caption-1 font-weight-600 grey--text text--darken-3">
                Adjusted Quantity:
                <v-chip
                  small
                  :color="
                    activeCount === targetQty && targetQty > 0
                      ? 'teal lighten-5'
                      : 'orange lighten-5'
                  "
                  :text-color="
                    activeCount === targetQty && targetQty > 0
                      ? 'teal darken-3'
                      : 'orange darken-3'
                  "
                  class="font-weight-700 ml-1"
                >
                  {{ activeCount }} / {{ targetQty }}
                </v-chip>
              </span>
            </div>
          </div>

          <!-- MAIN CONTENT -->
          <template v-if="targetQty > 0">
            <v-alert
              text
              dense
              color="red darken-4"
              icon="mdi-alert"
              class="rounded-lg mb-4 caption-1 font-weight-600"
            >
              Note: Please ensure that serial numbers are generated according to
              the receive quantity.
            </v-alert>

            <v-form
              ref="serialFormRff"
              lazy-validation
              v-on:submit.stop.prevent="saveSerials()"
            >
              <v-card flat class="custom-grey-border rounded-lg pa-4 mb-2">
                <!-- AUTO YES / NO -->
                <v-row class="align-center mb-2">
                  <v-col cols="12" md="7">
                    <label
                      class="caption text-uppercase font-weight-700 grey--text text--darken-2 d-block mb-1"
                    >
                      Auto Generated?
                    </label>
                    <v-radio-group
                      v-model="isAutoGenerated"
                      row
                      hide-details
                      class="mt-0"
                    >
                      <v-radio label="Yes" :value="1" color="cyan"></v-radio>
                      <v-radio
                        label="No (Manual / Import)"
                        :value="0"
                        color="cyan"
                      ></v-radio>
                    </v-radio-group>
                  </v-col>

                  <!-- IMPORT -->
                  <v-col
                    cols="12"
                    md="5"
                    class="text-md-right"
                    v-if="isAutoGenerated === 0"
                  >
                    <label
                      class="caption text-uppercase font-weight-700 grey--text text--darken-2 d-block mb-1"
                    >
                      Bulk Import File
                    </label>
                    <v-btn
                      depressed
                      color="cyan darken-1"
                      class="white--text font-weight-600 text-none"
                      v-on:click="importDialog = true"
                    >
                      <v-icon small left>mdi-upload</v-icon>
                      Import File
                    </v-btn>
                  </v-col>
                </v-row>

                <!-- PREFIX + NUMBER -->
                <v-row
                  class="align-center mt-2 mb-2"
                  v-if="isAutoGenerated === 1"
                >
                  <v-col cols="12" sm="2">
                    <label
                      class="caption text-uppercase font-weight-700 grey--text text--darken-2 d-block mb-1"
                    >
                      Prefix
                    </label>
                    <v-text-field
                      dense
                      outlined
                      color="cyan"
                      solo
                      flat
                      placeholder="Prefix (e.g. SN)"
                      maxlength="8"
                      v-model.trim="serial_prefix"
                      :rules="[
                        validateRules.minLength(serial_prefix, 'Prefix', 1),
                        validateRules.maxLength(serial_prefix, 'Prefix', 8),
                      ]"
                    />
                  </v-col>

                  <v-col cols="12" sm="2">
                    <label
                      class="caption text-uppercase font-weight-700 grey--text text--darken-2 d-block mb-1"
                    >
                      Start No.
                    </label>
                    <v-text-field
                      type="text"
                      inputmode="numeric"
                      dense
                      outlined
                      color="cyan"
                      solo
                      flat
                      placeholder="Starting number"
                      maxlength="6"
                      v-model.trim="serial_prefix_no"
                      :rules="[
                        validateRules.maxLength(serial_prefix_no, 'No.', 6),
                      ]"
                      v-on:keypress="isNumber($event)"
                    />
                  </v-col>

                  <!-- PREVIEW + GENERATE -->
                  <v-col cols="12" sm="4">
                    <label
                      class="caption text-uppercase font-weight-700 grey--text text--darken-2 d-block mb-1"
                    >
                      Preview
                    </label>
                    <div class="d-flex align-center">
                      <v-chip
                        label
                        small
                        color="grey lighten-3"
                        text-color="grey darken-3"
                        class="font-weight-700 text-uppercase mr-2"
                      >
                        {{ serial_prefix ? serial_prefix + "-" : ""
                        }}{{ serial_prefix_no || "" }}
                      </v-chip>
                      <v-btn
                        depressed
                        color="cyan darken-1"
                        class="white--text font-weight-600 text-none"
                        :disabled="!serial_prefix_no || targetQty <= 0"
                        v-on:click="generatePrefixes"
                      >
                        <v-icon small left>mdi-auto-fix</v-icon>
                        Generate
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>

                <!-- LIST OF GENERATED / IMPORTED SERIAL NUMBERS -->
                <v-row v-if="added_searial_numbers.length" class="mt-4">
                  <v-col
                    v-for="(row, i) in added_searial_numbers"
                    :key="i"
                    cols="12"
                    sm="6"
                    md="4"
                    lg="3"
                  >
                    <div class="d-flex align-center">
                      <v-text-field
                        solo
                        flat
                        v-model="row.text"
                        :placeholder="'Enter barcode ' + (i + 1)"
                        maxlength="35"
                        dense
                        outlined
                        hide-details
                        color="cyan"
                        class="font-weight-600"
                      ></v-text-field>
                      <v-btn
                        icon
                        small
                        color="red darken-1"
                        class="ml-1"
                        v-on:click="deleteNumber(i)"
                        title="Remove serial number"
                      >
                        <v-icon small>mdi-close</v-icon>
                      </v-btn>
                    </div>
                    <div
                      v-if="isDuplicateSerial(row.text)"
                      class="red--text font-size-12 mt-1"
                    >
                      Serial Number Already Exists
                    </div>
                    <div v-else-if="!(row.text || '').trim()" class="red--text">
                        Serial Number cannot be empty
                    </div>
                  </v-col>
                </v-row>
              </v-card>
            </v-form>
          </template>

          <!-- NO ITEM FOUND -->
          <template v-else>
            <div class="d-flex flex-column align-center justify-center py-8">
              <v-avatar color="cyan lighten-5" size="64" class="mb-3">
                <v-icon size="36" color="cyan darken-2">mdi-barcode-off</v-icon>
              </v-avatar>
              <h3 class="subtitle-1 font-weight-700 color-custom-blue mb-1">
                No Serial Numbers Required
              </h3>
              <p class="caption grey--text text--darken-1 mb-0">
                There are no serial numbers to generate or manage at the moment.
              </p>
            </div>
          </template>
        </div>
      </template>

      <template v-slot:action>
        <v-btn
          depressed
          color="cyan darken-1"
          class="px-6 white--text font-weight-600 custom-bold-button"
          v-on:click="saveSerials"
        >
          <v-icon small left>mdi-check-circle</v-icon>
          Save
        </v-btn>

        <v-btn
          depressed
          class="px-6 custom-grey-border custom-bold-button"
          v-on:click="$emit('close')"
        >
          Close
        </v-btn>
      </template>
    </Dialog>

    <!-- IMPORT DIALOG -->
    <ImportTemplate
      :dialog="importDialog"
      title="Import Serial Number"
      sample-url="/import-samples/productbarcode.xlsx"
      api-url="serial-number/import"
      @close="importDialog = false"
      @response="getData"
    />
  </div>
</template>

<script>
import CommonMixin from "@/core/plugins/common-mixin";
import ValidationMixin from "@/core/plugins/validation-mixin";
import ImportTemplate from "@/view/pages/partials/Import-Template.vue";
import Dialog from "@/view/pages/partials/Dialog.vue";
import { ErrorEventBus, InitializeError } from "@/core/lib/message.lib";
import { v4 as uuidv4 } from "uuid";

export default {
  name: "po-serial-number-dialog",
  mixins: [CommonMixin, ValidationMixin],
  components: {
    ImportTemplate,
    Dialog,
  },
  props: {
    dialogStatus: {
      type: Boolean,
      default: false,
    },
    itemData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      importDialog: false,
      isAutoGenerated: 1,
      serial_prefix: "",
      serial_prefix_no: "",
      added_searial_numbers: [],
    };
  },
  computed: {
    dialogWidth() {
      return this.lodash.toSafeInteger((document.body.clientWidth / 100) * 50);
    },
    pendingQty() {
      return parseInt(this.itemData?.to_be_received_quantity) || 0;
    },
    targetQty() {
      const q = parseInt(this.itemData?.quantity);
      return q > 0 ? q : 0;
    },
    activeCount() {
      return this.added_searial_numbers.length;
    },
    duplicateSerials() {
      return this.added_searial_numbers.reduce((duplicates, serial) => {
        const value = (serial.text || "").trim().toLowerCase();
        if (!value) return duplicates;
        duplicates[value] = (duplicates[value] || 0) + 1;
        return duplicates;
      }, {});
    },
  },
  watch: {
    dialogStatus: {
      immediate: true,
      handler(isOpen) {
        if (isOpen) {
          this.initData();
        }
      },
    },
  },
  methods: {
    isDuplicateSerial(value) {
      const normalized = (value || "").trim().toLowerCase();
      return Boolean(normalized && this.duplicateSerials[normalized] > 1);
    },
    getProductName() {
      if (this.itemData && this.itemData.product) {
        const p = this.itemData.product;
        return `${p.name || ""} ${p.barcode ? "(" + p.barcode + ")" : ""}`;
      }
      return "Product";
    },
    initData() {
      this.isAutoGenerated = 1;
      this.serial_prefix = this.itemData?.serial_prefix || "";
      this.serial_prefix_no = this.itemData?.serial_prefix_no || "";
      this.importDialog = false;

      const existing = this.itemData?.serial_numbers;
      this.added_searial_numbers = [];
      if (Array.isArray(existing) && existing.length > 0) {
        this.added_searial_numbers = existing.map((s) => ({
          uuid: uuidv4(),
          text: typeof s === "object" ? s.text : s,
          editable: true,
          error: false,
        }));
      } else if (typeof existing === "string" && existing.trim()) {
        this.added_searial_numbers = existing
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
          .map((text) => ({
            uuid: uuidv4(),
            text,
            editable: true,
            error: false,
          }));
      }

      if (
        !this.serial_prefix &&
        !this.serial_prefix_no &&
        this.added_searial_numbers.length > 0
      ) {
        const firstSerial = this.added_searial_numbers[0].text || "";
        const match = firstSerial.match(/^(.*)-([0-9]+)$/);
        if (match) {
          this.serial_prefix = match[1];
          this.serial_prefix_no = match[2];
        }
      }
    },
    getData(data) {
      if (Array.isArray(data)) {
        this.added_searial_numbers = data.map((d) => ({
          uuid: uuidv4(),
          text: typeof d === "object" ? d.text || d.barcode : d,
          editable: true,
          error: false,
        }));
      }
    },
    generatePrefixes() {
      const adjusted = this.targetQty;
      if (!this.serial_prefix_no || adjusted <= 0) return;

      this.added_searial_numbers = [];
      const prefixNoStr = String(this.serial_prefix_no).trim();

      for (let i = 1; i <= adjusted; i++) {
        const prefix = this.serial_prefix ? `${this.serial_prefix}-` : "";
        const num = String(Number(prefixNoStr) + i - 1).padStart(
          prefixNoStr.length,
          "0"
        );

        this.added_searial_numbers.push({
          uuid: uuidv4(),
          text: `${prefix}${num}`,
          editable: true,
          error: false,
        });
      }
    },
    deleteNumber(i) {
      this.added_searial_numbers.splice(i, 1);
    },
    saveSerials() {
      if (this.added_searial_numbers.some((s) => !(s.text || "").trim())) {
        ErrorEventBus.$emit(
          "update:error",
          InitializeError("Serial Number cannot be empty")
        );
        return;
      }

      const validSerials = this.added_searial_numbers
        .map((s) => (s.text || "").trim())
        .filter(Boolean);

      if (validSerials.length !== this.targetQty) {
        ErrorEventBus.$emit(
          "update:error",
          InitializeError(
            "Ensure that the adjusted quantity entered matches the Serial Number count"
          )
        );
        return;
      }

      if (validSerials.some((serial) => this.isDuplicateSerial(serial))) {
        ErrorEventBus.$emit(
          "update:error",
          InitializeError("Serial Number Already Exists")
        );
        return;
      }

      this.$emit("save", {
        serial_numbers: validSerials,
        quantity: this.targetQty,
        serial_prefix: this.serial_prefix,
        serial_prefix_no: this.serial_prefix_no,
      });
    },
  },
};
</script>
